// InlineAlertNotifier.js
/**
 * InlineAlertNotifier
 * -------------------
 * Utility class for rendering Bootstrap inline alerts inside a target container.
 *
 * Responsibilities:
 * - Build Bootstrap 5 alert components dynamically
 * - Optionally sanitize HTML content using DOMPurify
 * - Insert alerts into a target DOM element
 * - Automatically dismiss alerts after a configurable delay
 * - Pause auto-dismiss when the alert is hovered
 *
 * Features:
 * - Supports Bootstrap contextual types (success, danger, warning, info, etc.)
 * - Optional HTML sanitization for safe rich messages
 * - Auto-dismiss with hover pause
 * - Graceful fallback if Bootstrap JS is not available
 *
 * Alert types supported:
 *  - success
 *  - danger / error
 *  - warning
 *  - info
 *  - primary
 *  - secondary
 *  - dark
 *  - light
 *
 * Example usage:
 *
 * import DOMPurify from 'dompurify';
 * import InlineAlertNotifier from './InlineAlertNotifier.js';
 *
 * const domPurify = DOMPurify(window);
 *
 * const inlineAlerts = new InlineAlertNotifier({
 *     domPurify,
 *     autoDismissMs: 6000
 * });
 *
 * const container = document.querySelector('#result-container');
 *
 * inlineAlerts.show(container, {
 *     type: 'success',
 *     message: serverMessage,
 *     dismissMs: 4000
 * });
 *
 * Example with HTML message:
 *
 * inlineAlerts.show(container, {
 *     type: 'warning',
 *     message: 'Controlla i <strong>dati inseriti</strong> prima di continuare.',
 *     allowHtml: true
 * });
 */
class InlineAlertNotifier {
    constructor({
        domPurify,
        translator,
        autoDismissMs = 6000,
    } = {}) {
        if (!domPurify) throw new Error('InlineAlertNotifier: domPurify is required');
        if (!translator) throw new Error('InlineAlertNotifier: translator is required');

        this.domPurify = domPurify;
        this.translator = translator;
        this.autoDismissMs = autoDismissMs;

        this._purifyConfig = Object.freeze({
            ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'u', 'br', 'a', 'span', 'small', 'code', 'p'],
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
        });

        this.domPurify.addHook('afterSanitizeAttributes', (node) => {
            if (node.tagName === 'A') {
                node.setAttribute('rel', 'noopener noreferrer');
                // node.setAttribute('target', '_blank');
            }
        });
    }

    sanitizeHtml(html) {
        return this.domPurify.sanitize(String(html || ''), this._purifyConfig);
    }

    normalizeAlertClass(type) {
        type = (type || 'info').toLowerCase();

        let cls = 'alert-info';
        if (type === 'success') cls = 'alert-success';
        else if (type === 'danger' || type === 'error') cls = 'alert-danger';
        else if (type === 'warning') cls = 'alert-warning';
        else if (type === 'primary') cls = 'alert-primary';
        else if (type === 'secondary') cls = 'alert-secondary';
        else if (type === 'dark') cls = 'alert-dark';
        else if (type === 'light') cls = 'alert-light';

        return cls;
    }

    build(type, message, allowHtml = false) {
        const cls = this.normalizeAlertClass(type);

        const alert = document.createElement('div');
        alert.className = `alert ${cls} alert-dismissible mb-2 rounded-3 shadow-sm border-0 fade`;
        alert.setAttribute('role', 'alert');
        alert.style.pointerEvents = 'auto';

        const msg = document.createElement('div');
        msg.className = 'me-2';

        if (allowHtml) {
            msg.innerHTML = this.sanitizeHtml(message || '');
        } else {
            msg.textContent = message || '';
        }

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'btn-close';
        closeBtn.setAttribute('data-bs-dismiss', 'alert');
        closeBtn.setAttribute('aria-label', this.translator.trans('common.close'));

        alert.appendChild(msg);
        alert.appendChild(closeBtn);

        return alert;
    }

    setupAutoDismiss(node, closeFn, ms) {
        let remaining = ms;
        let start = Date.now();
        let timer = remaining > 0 ? setTimeout(closeFn, remaining) : null;

        node.addEventListener('mouseenter', () => {
            if (timer) {
                clearTimeout(timer);
                timer = null;
                remaining -= (Date.now() - start);
            }
        });

        node.addEventListener('mouseleave', () => {
            if (remaining > 0) {
                start = Date.now();
                timer = setTimeout(closeFn, remaining);
            }
        });
    }

    show(targetEl, {
        type = 'info',
        message = '',
        allowHtml = false,
        dismissMs = null,
    } = {}) {
        if (!(targetEl instanceof Element)) {
            throw new Error('InlineAlertNotifier: targetEl must be a DOM element');
        }

        const resolvedDismissMs = typeof dismissMs === 'number'
            ? dismissMs
            : this.autoDismissMs;

        const alert = this.build(type, message, allowHtml);
        targetEl.prepend(alert);

        requestAnimationFrame(() => {
            alert.classList.add('show');
        });

        const closeInline = () => {
            try {
                if (window.bootstrap && bootstrap.Alert) {
                    bootstrap.Alert.getOrCreateInstance(alert).close();
                } else {
                    alert.classList.remove('show');
                    setTimeout(() => alert.remove(), 150);
                }
            } catch (_) {
                alert.remove();
            }
        };

        this.setupAutoDismiss(alert, closeInline, resolvedDismissMs);

        return alert;
    }
}

export default InlineAlertNotifier;